"use client";

import type { CSSProperties, MouseEvent, TouchEvent } from "react";
import { useEffect, useState } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";

export function HeroSection() {
  const { scrollY } = useScroll();
  const prefersReducedMotion = useReducedMotion();
  const [isMobile, setIsMobile] = useState(false);
  const motionScale = prefersReducedMotion ? 0.35 : 1;
  const bgY = useTransform(scrollY, [0, 600], [0, 60 * motionScale]);
  const orbY = useTransform(scrollY, [0, 600], [0, 180 * motionScale]);
  const headlineOpacity = useTransform(scrollY, [0, 300], [1, prefersReducedMotion ? 0.85 : 0.6]);
  const ctaStyle = { "--x": "50%", "--y": "50%" } as CSSProperties;

  useEffect(() => {
    const media = window.matchMedia("(min-width: 768px)");
    const update = () => setIsMobile(!media.matches);
    update();
    media.addEventListener("change", update);
    return () => {
      media.removeEventListener("change", update);
    };
  }, []);

  const resetCta = (target: HTMLAnchorElement) => {
    target.style.setProperty("--x", "50%");
    target.style.setProperty("--y", "50%");
  };

  const handleCtaMove = (event: MouseEvent<HTMLAnchorElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    event.currentTarget.style.setProperty("--x", `${x}px`);
    event.currentTarget.style.setProperty("--y", `${y}px`);
  };

  const handleCtaTouch = (event: TouchEvent<HTMLAnchorElement>) => {
    const touch = event.touches[0];
    if (!touch) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    event.currentTarget.style.setProperty("--x", `${x}px`);
    event.currentTarget.style.setProperty("--y", `${y}px`);
  };

  const handleCtaLeave = (event: MouseEvent<HTMLAnchorElement>) => {
    resetCta(event.currentTarget);
  };

  const handleCtaTouchEnd = (event: TouchEvent<HTMLAnchorElement>) => {
    resetCta(event.currentTarget);
  };

  return (
    <section className="relative overflow-hidden px-4 pb-32 pt-16 md:pb-40 md:pt-32">
      <div className="pointer-events-none absolute inset-0">
        <motion.div
          style={{ y: bgY }}
          className="absolute inset-0 z-0 bg-gradient-to-r from-indigo-100 via-white to-violet-100 will-change-transform"
        />
        <div className="pointer-events-none absolute left-1/2 top-[-200px] z-20 -translate-x-1/2">
          <motion.div
            style={{ y: orbY }}
            className="h-[900px] w-[900px] rounded-full bg-gradient-to-br from-indigo-300/40 to-violet-300/30 blur-3xl will-change-transform"
          />
        </div>
      </div>

      <div className="relative z-30 mx-auto max-w-5xl text-center">
        <h1 className="mb-6">
          <span className="block text-slate-900 text-3xl md:text-5xl lg:text-6xl font-semibold leading-tight">
            Turn book insights into
          </span>

          <motion.span
            style={{ opacity: headlineOpacity }}
            className="
              block
              pb-[0.08em]
              text-4xl md:text-6xl lg:text-7xl
              font-bold
              leading-[1.15]
              bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600
              bg-clip-text text-transparent
            "
          >
            scroll-stopping videos
          </motion.span>
        </h1>

        <p className="mx-auto mb-4 max-w-3xl text-lg leading-relaxed text-slate-600 md:text-xl">
          BookToVideo uses AI to transform the core ideas of any book into short, ready-to-post videos for YouTube
          Shorts, TikTok, and Instagram Reels.
        </p>

        <p className="mb-8 text-sm text-slate-500">Original summaries. No copied text. No reading on camera.</p>

        <div className="flex flex-col items-center gap-4">
          <motion.a
            href="#waitlist"
            onPointerMove={handleCtaMove}
            onPointerDown={handleCtaMove}
            onPointerLeave={handleCtaLeave}
            onTouchStart={handleCtaTouch}
            onTouchMove={handleCtaTouch}
            onTouchEnd={handleCtaTouchEnd}
            style={ctaStyle}
            initial={false}
            animate={
              !prefersReducedMotion && isMobile
                ? { scale: [1, 1.02, 1] }
                : { scale: 1 }
            }
            transition={
              !prefersReducedMotion && isMobile
                ? { duration: 0.9, ease: "easeOut", times: [0, 0.45, 1], delay: 0.6 }
                : undefined
            }
            className="relative z-40 inline-flex items-center justify-center overflow-hidden rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 px-8 py-4 text-lg font-semibold text-white shadow-lg transition-opacity duration-300 before:absolute before:inset-0 before:bg-[radial-gradient(circle_at_var(--x)_var(--y),rgba(255,255,255,0.35),transparent_60%)] before:opacity-0 before:transition-opacity before:duration-300 before:content-[''] hover:opacity-90 hover:before:opacity-100 focus-visible:before:opacity-100 active:before:opacity-100 md:shadow-none"
          >
            <span className="relative z-10">Get early access</span>
          </motion.a>
          <p className="text-xs text-slate-500">Be among the first creators shaping BookToVideo.</p>
        </div>
      </div>
    </section>
  );
}
